/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (default: AUD)
 * @param locale - Locale for formatting (default: en-AU)
 * @param options - Optional formatting options (minimumFractionDigits, maximumFractionDigits)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'AUD',
  locale: string = 'en-AU',
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: options?.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits,
  }).format(amount);

  // Add space after currency symbol (e.g., "$1,234" -> "$ 1,234")
  return formatted.replace(/(\$|€|£|¥)(\d)/, '$1 $2');
}

export function parsePriceAmount(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value ? value : undefined;
  const s = String(value).trim().replace(/[^0-9.-]+/g, '');
  if (s === '' || s === '-' || s === '.') return undefined;
  const n = Number(s);
  return n ? n : undefined;
}

/**
 * Format a date in a human-readable format
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  return new Intl.DateTimeFormat('en-AU', options || defaultOptions).format(d);
}

export function formatTime(date: Date | string | number): string {
  const d =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date for "Updated on" display: "Dec 12, 2025 | 04:32 PM"
 * @param date - Date to format
 * @returns Formatted string (date | 12-hour time)
 */
export function formatUpdatedOn(date: Date | string | number): string {
  const d =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  if (isNaN(d.getTime())) return 'Invalid date';

  const dateStr = new Intl.DateTimeFormat('en-AU', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
  const timeStr = new Intl.DateTimeFormat('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
  return `${dateStr} | ${timeStr}`;
}

export function formatStartsIn(totalSeconds: number): string {
  if (totalSeconds <= 0) return '00h:00m:00s';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}h:${pad(m)}m:${pad(s)}s`;
}

/**
 * Format time in seconds to a readable countdown string for sliders/labels.
 * - If >= 1 hour: "1h:23m:45s"
 * - If < 1 hour: "23m:45s"
 * - If < 1 minute: "45s"
 * @param totalSeconds - Duration in seconds
 * @returns Formatted countdown string
 */
export function formatTimeSlider(totalSeconds: number): string {
  if (totalSeconds <= 0) return '0s';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h:${minutes.toString().padStart(2, '0')}m:${seconds.toString().padStart(2, '0')}s`;
  }

  if (minutes > 0) {
    return `${minutes}m:${seconds.toString().padStart(2, '0')}s`;
  }

  return `${seconds}s`;
}

export function formatTimeTimer(totalSeconds: number): string {
  if (totalSeconds <= 0) return '00:00';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Format a date relative to now (e.g., "2 mins ago", "3 hours ago")
 * @param date - Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | string | number): string {
  try {
    const d =
      typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;

    // Check if date is valid
    if (isNaN(d.getTime())) {
      return 'Invalid date';
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

    // Handle future dates
    if (diffInSeconds < 0) return 'just now';

    if (diffInSeconds < 60) return 'just now';

    const minutes = Math.floor(diffInSeconds / 60);
    if (diffInSeconds < 3600) {
      return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
    }

    const hours = Math.floor(diffInSeconds / 3600);
    if (diffInSeconds < 86400) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    const days = Math.floor(diffInSeconds / 86400);
    if (diffInSeconds < 604800) {
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    return formatDate(d);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid date';
  }
}

/**
 * Truncate a string to a maximum length
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to add when truncated (default: "...")
 * @returns Truncated string
 */
export function truncate(
  str: string,
  maxLength: number,
  suffix: string = '...',
): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Capitalize the first letter of a string
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert a string to kebab-case
 * @param str - String to convert
 * @returns Kebab-cased string
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

/**
 * Format file size in bytes to human-readable format (e.g., "150kb", "2mb")
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "150kb", "2mb", "1.5gb")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0b';
  const k = 1024;
  const sizes = ['b', 'kb', 'mb', 'gb'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = Math.round((bytes / Math.pow(k, i)) * 100) / 100;
  return `${size % 1 === 0 ? size.toString() : size.toFixed(1)}${sizes[i]}`;
}

/**
 * Format a chat message timestamp with smart date handling
 * - Today: shows only time (e.g., "5:15 PM")
 * - Yesterday: shows "Yesterday, 5:15 PM"
 * - Older: shows date and time (e.g., "16 May, 5:15 PM")
 * @param date - Date to format
 * @returns Formatted timestamp string
 */
export function formatChatTimestamp(date: Date | string | number): string {
  const d =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  // Calculate difference in days
  const diffTime = today.getTime() - messageDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Format time in 12-hour format (e.g., "5:15 PM")
  const timeStr = d.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Today: just show time
  if (diffDays === 0) {
    return timeStr;
  }

  // Yesterday: show "Yesterday, 5:15 PM"
  if (diffDays === 1) {
    return `Yesterday, ${timeStr}`;
  }

  // Older dates: show "16 May, 5:15 PM"
  const day = d.getDate();
  const month = d.toLocaleString('en-AU', { month: 'short' });
  return `${day} ${month}, ${timeStr}`;
}
